import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useChecklist } from '../../context/ChecklistContext';
import { useApp } from '../../context/AppContext';

const TemplateCreateDialog = ({ open, onOpenChange, onSuccess }) => {
  const { createTemplate } = useChecklist();
  const { allAreas } = useApp();
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    appliesToArea: 'all',
    frequency: '',
    defaultOwnerRole: '',
    criticality: ''
  });
  const [errors, setErrors] = useState({});

  const categories = [
    'GMP Rounds',
    'Production',
    'QC',
    'Utilities',
    'Calibration',
    'Storage',
    'Maintenance'
  ];

  const frequencies = [
    'Daily',
    'Weekly',
    'Monthly',
    'Ad-hoc'
  ];

  const ownerRoles = [
    'QA_OFFICER',
    'PROD_SUP',
    'QC_ANALYST',
    'ENGINEERING',
    'OPERATOR',
    'WAREHOUSE'
  ];

  const criticalities = [
    'Low',
    'Medium',
    'High',
    'Critical'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Template name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.frequency) newErrors.frequency = 'Frequency is required';
    if (!formData.defaultOwnerRole) newErrors.defaultOwnerRole = 'Owner role is required';
    if (!formData.criticality) newErrors.criticality = 'Criticality is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const template = createTemplate(formData);
    setFormData({
      name: '',
      category: '',
      appliesToArea: 'all',
      frequency: '',
      defaultOwnerRole: '',
      criticality: ''
    });
    setErrors({});
    onOpenChange(false);
    if (onSuccess) onSuccess(template);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-white border-slate-200">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Create New Checklist Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-slate-700">Template Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={errors.name ? 'border-red-300' : 'border-slate-200'}
              placeholder="e.g., Daily GMP Round Checklist"
            />
            {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="category" className="text-slate-700">Category *</Label>
            <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
              <SelectTrigger className={errors.category ? 'border-red-300' : 'border-slate-200'}>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && <p className="text-xs text-red-600">{errors.category}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="appliesToArea" className="text-slate-700">Applies To Area</Label>
            <Select value={formData.appliesToArea} onValueChange={(v) => handleChange('appliesToArea', v)}>
              <SelectTrigger className="border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {allAreas.map(area => (
                  <SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="frequency" className="text-slate-700">Frequency *</Label>
            <Select value={formData.frequency} onValueChange={(v) => handleChange('frequency', v)}>
              <SelectTrigger className={errors.frequency ? 'border-red-300' : 'border-slate-200'}>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                {frequencies.map(freq => (
                  <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.frequency && <p className="text-xs text-red-600">{errors.frequency}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="defaultOwnerRole" className="text-slate-700">Default Owner Role *</Label>
            <Select value={formData.defaultOwnerRole} onValueChange={(v) => handleChange('defaultOwnerRole', v)}>
              <SelectTrigger className={errors.defaultOwnerRole ? 'border-red-300' : 'border-slate-200'}>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {ownerRoles.map(role => (
                  <SelectItem key={role} value={role}>{role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.defaultOwnerRole && <p className="text-xs text-red-600">{errors.defaultOwnerRole}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="criticality" className="text-slate-700">Criticality *</Label>
            <Select value={formData.criticality} onValueChange={(v) => handleChange('criticality', v)}>
              <SelectTrigger className={errors.criticality ? 'border-red-300' : 'border-slate-200'}>
                <SelectValue placeholder="Select criticality" />
              </SelectTrigger>
              <SelectContent>
                {criticalities.map(crit => (
                  <SelectItem key={crit} value={crit}>{crit}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.criticality && <p className="text-xs text-red-600">{errors.criticality}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-200">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="bg-[#fd4816] hover:bg-[#ea380c] text-white">
            Create Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateCreateDialog;

